/**
 * Graph API Service - Frontend API client for graph visualization
 * 
 * PHASE 5 IMPLEMENTATION
 * Provides API calls for entity graphs, process trees, and timelines.
 */

import apiClient from './apiClient';

export interface GraphNode {
  id: string;
  type: string;
  data: {
    label: string;
    type: string;
    risk_score?: number;
    criticality?: string;
    status?: string;
    attributes?: Record<string, any>;
    first_seen?: string;
    last_seen?: string;
  };
  position: { x: number; y: number };
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  label: string;
  data: {
    relationship_type: string;
    confidence?: number;
    attributes?: Record<string, any>;
  };
  animated?: boolean;
}

export interface GraphResponse {
  nodes: GraphNode[];
  edges: GraphEdge[];
  metadata?: {
    alert_id: string;
    entity_count: number;
    relationship_count: number;
    alert_severity?: string;
    alert_title?: string;
  };
}

export interface TimelineEvent {
  id: string;
  timestamp: string;
  event_type: string;
  event_category: string;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  type: 'anomaly' | 'alert' | 'incident' | 'normal';
  entity_id: string;
  entity_name: string;
  entity_type: string;
  risk_score?: number;
  riskScore?: number; // Alias for compatibility
  action?: string;
}

export interface RiskDataPoint {
  timestamp: Date;
  risk_score: number;
  riskScore?: number; // Alias for compatibility
  label?: string;
  eventCount?: number;
}

export interface TimelineResponse {
  events: TimelineEvent[];
}

export interface ProcessTreeNode {
  id: string;
  name: string;
  attributes: Record<string, any>;
  risk_score?: number;
  children: ProcessTreeNode[];
}

export interface ProcessTreeResponse {
  nodes: GraphNode[];
  edges: GraphEdge[];
  tree: ProcessTreeNode | null;
}

export interface PathResponse {
  path: string[] | null;
  nodes: GraphNode[];
  edges: GraphEdge[];
  length?: number;
  message?: string;
}

export interface SearchResponse {
  results: GraphNode[];
  count: number;
  query: string;
}

/**
 * Fetch user risk timeline data
 */
export const fetchUserRiskTimeline = async (
  userId: string,
  dateRange?: [Date, Date]
): Promise<{ events: TimelineEvent[]; dataPoints: RiskDataPoint[] }> => {
  const params = new URLSearchParams();
  if (dateRange) {
    params.append('start_date', dateRange[0].toISOString());
    params.append('end_date', dateRange[1].toISOString());
  }
  const response = await apiClient.get(
    `/api/ueba/users/${userId}/risk-timeline?${params.toString()}`
  );
  return response.data;
};

export const graphApi = {
  /**
   * Get complete entity graph for an alert
   */
  getAlertEntities: async (alertId: string): Promise<GraphResponse> => {
    const response = await apiClient.get(`/api/graph/alerts/${alertId}/entities`);
    return response.data;
  },

  /**
   * Get process tree for an alert
   */
  getProcessTree: async (alertId: string): Promise<ProcessTreeResponse> => {
    const response = await apiClient.get(`/api/graph/alerts/${alertId}/process-tree`);
    return response.data;
  },

  /**
   * Get timeline of events for an alert
   */
  getTimeline: async (alertId: string): Promise<TimelineResponse> => {
    const response = await apiClient.get(`/api/graph/alerts/${alertId}/timeline`);
    return response.data;
  },

  /**
   * Traverse graph from an entity
   */
  traverseGraph: async (
    entityId: string,
    depth: number = 3,
    relationshipTypes?: string[]
  ): Promise<GraphResponse> => {
    const params = new URLSearchParams({
      depth: depth.toString(),
    });
    
    if (relationshipTypes && relationshipTypes.length > 0) {
      relationshipTypes.forEach(type => {
        params.append('relationship_types', type);
      });
    }
    
    const response = await apiClient.get(
      `/api/graph/entities/${entityId}/traverse?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Find shortest path between two entities
   */
  findPath: async (sourceId: string, targetId: string): Promise<PathResponse> => {
    const response = await apiClient.get(
      `/api/graph/entities/${sourceId}/path/${targetId}`
    );
    return response.data;
  },

  /**
   * Search entities
   */
  searchEntities: async (
    query: string,
    entityTypes?: string[],
    timeRange?: string,
    limit: number = 50
  ): Promise<SearchResponse> => {
    const params = new URLSearchParams({
      query,
      limit: limit.toString(),
    });
    
    if (entityTypes && entityTypes.length > 0) {
      entityTypes.forEach(type => {
        params.append('entity_types', type);
      });
    }
    
    if (timeRange) {
      params.append('time_range', timeRange);
    }
    
    const response = await apiClient.get(
      `/api/graph/entities/search?${params.toString()}`
    );
    return response.data;
  },
};
